import { Node } from '@react-types/shared';
import { omit } from 'lodash-es';
import React, { useRef } from 'react';
import { mergeProps, useMenuItem } from 'react-aria';
import { TreeState } from 'react-stately';
import { SetRequired } from 'type-fest';
import { MenuListItem } from 'components/MenuList';
import { LinkItemProps } from '../LinkItem';

export interface MenuLinkItemProps<T, Link extends React.ElementType> {
  item: Omit<Node<T>, 'props'> & {
    props?: SetRequired<LinkItemProps<T, Link>, 'linkComponent'>;
  };
  state: TreeState<T>;
}

/**
 * A menu item that renders as a link.  This component ensures that menu
 * items use semantically correct HTML when redirecting.
 *
 * @see https://github.com/adobe/react-spectrum/issues/1244
 */
export function MenuLinkItem<T, Link extends React.ElementType>({
  item,
  state,
}: MenuLinkItemProps<T, Link>): React.ReactElement | null {
  const ref = useRef<HTMLAnchorElement>(null);
  const { menuItemProps, isDisabled } = useMenuItem(
    { key: item.key },
    state,
    ref,
  );

  if (!item.props) {
    return null;
  }

  const {
    children,
    icon,
    title,
    textValue,
    'aria-label': ariaLabel,
    childItems,
    hasChildItems,
    linkComponent,
    ...rest
  } = item.props;

  const Component: React.ElementType = linkComponent;

  const mergedProps = mergeProps(omit(menuItemProps, 'role'), rest);

  return (
    <li role="none">
      <MenuListItem
        as={Component}
        {...mergedProps}
        {...{ icon, isDisabled }}
        ref={ref}
      >
        {item.rendered}
      </MenuListItem>
    </li>
  );
}

export default MenuLinkItem;
